import type { ReactNode } from "react";
import { createUseStyles } from "react-jss";
import type { VetBoatsTheme } from "../../theme";
import { Button } from "./Button";
import { Notice } from "./Notice";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  loading: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: theme.color.muted,
    padding: "16px 0",
  },
  spinner: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: `2px solid ${theme.color.border}`,
    borderTopColor: theme.color.primary,
    animation: "$spin 0.8s linear infinite",
  },
  "@keyframes spin": { to: { transform: "rotate(360deg)" } },
  empty: {
    textAlign: "center",
    padding: "28px 16px",
    color: theme.color.muted,
    border: `1px dashed ${theme.color.border}`,
    borderRadius: theme.radius,
  },
  emptyTitle: { margin: "0 0 6px", color: theme.color.text, fontSize: 16 },
  emptyAction: { marginTop: 14 },
}));

export const Loading = ({ label = "Loading…" }: { label?: string }) => {
  const classes = useStyles();
  return (
    <div className={classes.loading} role="status">
      <span className={classes.spinner} aria-hidden />
      {label}
    </div>
  );
};

export const LoadError = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) => (
  <div>
    <Notice kind="error">{message}</Notice>
    <Button variant="secondary" small onClick={onRetry}>
      Try again
    </Button>
  </div>
);

interface EmptyStateProps {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}

export const EmptyState = ({ title, children, action }: EmptyStateProps) => {
  const classes = useStyles();
  return (
    <div className={classes.empty}>
      <h3 className={classes.emptyTitle}>{title}</h3>
      {children}
      {action && <div className={classes.emptyAction}>{action}</div>}
    </div>
  );
};
